import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus, Trash2 } from 'lucide-react';
import { rateChartAPI } from '../services/api';

// WORK DETAILS Form (Pink Form) - Exact replica of paper form
// Predefined finishing work items with their default units and rate chart mapping
const FINISHING_WORKS_TEMPLATE = [
  { description: 'EDGE BANDING WORK', unit: 'Mts', qty: '', rate: '', details: '' },
  { description: '2MM EDGEBAND WORK', unit: 'Mts', qty: '', rate: '', details: '' },
  { description: '45MM EDGEBAND WORK', unit: 'Mts', qty: '', rate: '', details: '' },
  { description: 'SINGLE GROOVING', unit: 'Mts', qty: '', rate: '', details: '' },
  { description: 'GROOVING 8MM / LED GROOVING', unit: 'Mts', qty: '', rate: '', details: '' },
  { description: 'HANDLE PROFILE WORK', unit: 'Mts', qty: '', rate: '', details: '' },
  { description: 'EDGE PROFILE WORK', unit: 'Mts', qty: '', rate: '', details: '' },
  { description: 'MICA EDGE BANDING', unit: 'Nos', qty: '', rate: '', details: '' },
  { description: 'GLASS DOOR CUTTING', unit: 'Nos', qty: '', rate: '', details: '' },
  { description: 'DOUBLE WORK-S', unit: 'Nos', qty: '', rate: '', details: '' },
  { description: 'DOUBLE WORK-M', unit: 'Nos', qty: '', rate: '', details: '' },
  { description: 'DOUBLE WORK-L', unit: 'Nos', qty: '', rate: '', details: '' },
  { description: 'DOUBLE WORK-XL', unit: 'Nos', qty: '', rate: '', details: '' },
  { description: 'MITRE CUTTING', unit: 'Nos', qty: '', rate: '', details: '' },
  { description: 'HINGES HOLE', unit: 'Nos', qty: '', rate: '', details: '' },
  { description: 'TRIMMING', unit: 'Nos', qty: '', rate: '', details: '' },
  { description: 'DOOR BORDER WORK', unit: 'Box', qty: '', rate: '', details: '' },
  { description: 'MICA CUTTING', unit: '', qty: '', rate: '', details: '' },
  { description: 'CUTTING LIST PREPARATION', unit: '', qty: '', rate: '', details: '' },
  { description: 'MINI FIX HOLE WORK', unit: '', qty: '', rate: '', details: '' },
  { description: 'PACKING CHARGES', unit: '', qty: '', rate: '', details: '' },
  { description: 'EXTRA EDGE BAND', unit: '', qty: '', rate: '', details: '' },
  { description: 'MEASUREMENT CHARGE', unit: '', qty: '', rate: '', details: '' },
  { description: 'HANDLING CHARGES', unit: '', qty: '', rate: '', details: '' },
  { description: 'TRANSPORTATION', unit: '', qty: '', rate: '', details: '' },
];

const PREDEFINED_DESCRIPTIONS = FINISHING_WORKS_TEMPLATE.map(t => t.description.toUpperCase());

// Legacy description → new description mapping for backward compatibility
const LEGACY_DESC_MAP = {
  'EDGE BANDING': 'EDGE BANDING WORK',
  'EDGE BANDING 2MM': '2MM EDGEBAND WORK',
  'EDGE BANDING 45M': '45MM EDGEBAND WORK',
  'DOUBLE GROOVING': 'GROOVING 8MM / LED GROOVING',
  'EDGE PROFILEWORK': 'EDGE PROFILE WORK',
  'MINI FIX HOLE CUTTING': 'MINI FIX HOLE WORK',
  'PACKING': 'PACKING CHARGES',
  'HANDLING CHARGE': 'HANDLING CHARGES',
  'TRANS PORTATION': 'TRANSPORTATION',
  'HINGES': 'HINGES HOLE',
  'HOLE MINI FIX': 'MINI FIX HOLE WORK',
  'HOLE CUTTING': 'MINI FIX HOLE WORK',
  'MINI FIX HOLE': 'MINI FIX HOLE WORK',
  'EDGEBANDING': 'EDGE BANDING WORK',
  'EDGE BAND': 'EDGE BANDING WORK',
  '2MM EDGE BANDING': '2MM EDGEBAND WORK',
  '45MM EDGE BANDING': '45MM EDGEBAND WORK',
  'GROOVING': 'SINGLE GROOVING',
  'LED GROOVING': 'GROOVING 8MM / LED GROOVING',
};

const WorkDetailsForm = forwardRef(({ initialData, existingFinishingLines }, ref) => {
  const [formData, setFormData] = useState(() => {
    // Merge existing finishing lines into template
    let works = FINISHING_WORKS_TEMPLATE.map(item => ({ ...item }));
    let customWorks = [];

    if (existingFinishingLines && existingFinishingLines.length > 0) {
      existingFinishingLines.forEach(line => {
        const upperDesc = line.description?.toUpperCase()?.trim() || '';
        // Try direct match first, then legacy mapping, then partial match
        let templateIdx = works.findIndex(w => w.description.toUpperCase() === upperDesc);
        if (templateIdx < 0) {
          const mappedDesc = LEGACY_DESC_MAP[upperDesc];
          if (mappedDesc) {
            templateIdx = works.findIndex(w => w.description.toUpperCase() === mappedDesc.toUpperCase());
          }
        }
        if (templateIdx < 0) {
          // Partial match: find template item that contains the old description or vice versa
          templateIdx = works.findIndex(w => 
            w.description.toUpperCase().includes(upperDesc) || upperDesc.includes(w.description.toUpperCase())
          );
        }
        if (templateIdx >= 0) {
          // Predefined item - populate its fields
          works[templateIdx] = {
            ...works[templateIdx],
            qty: line.qty?.toString() || '',
            rate: line.rate?.toString() || '',
            details: line.details || '',
            unit: line.unit || works[templateIdx].unit
          };
        } else {
          // Custom entry
          customWorks.push({
            description: line.description || '',
            unit: line.unit || 'Nos',
            qty: line.qty?.toString() || '',
            rate: line.rate?.toString() || '',
            details: line.details || '',
            isCustom: true
          });
        }
      });
    }

    return {
      cutting_qty: '',
      pressing_qty: '',
      total_qty: '',
      ...initialData,
      finishing_works: works,
      custom_works: customWorks
    };
  });

  // Auto-fill rates from finishing rate chart AND add any new items from chart
  useEffect(() => {
    const loadRates = async () => {
      try {
        const finishingRates = await rateChartAPI.getAll(false, 'finishing');
        const rateLookup = {};
        const chartDescs = new Set();
        for (const item of finishingRates) {
          rateLookup[item.description.toUpperCase()] = item.rate;
          chartDescs.add(item.description.toUpperCase());
        }
        setFormData(prev => {
          // Auto-fill rates for existing items
          let updatedWorks = prev.finishing_works.map(fw => {
            const matchedRate = rateLookup[fw.description.toUpperCase()];
            if (matchedRate !== undefined && matchedRate > 0) {
              // Only override if current rate is empty/0
              if (!fw.rate || fw.rate === '' || fw.rate === '0') {
                return { ...fw, rate: matchedRate.toString() };
              }
            }
            return fw;
          });
          
          // Add any NEW items from rate chart that aren't in the template
          const existingDescs = new Set(updatedWorks.map(w => w.description.toUpperCase()));
          for (const item of finishingRates) {
            if (!existingDescs.has(item.description.toUpperCase())) {
              updatedWorks.push({
                description: item.description,
                unit: 'Nos',
                qty: '',
                rate: item.rate > 0 ? item.rate.toString() : '',
                details: '',
              });
            }
          }
          
          return { ...prev, finishing_works: updatedWorks };
        });
      } catch (err) {
        console.error('Failed to load finishing rates:', err);
      }
    };
    loadRates();
  }, []);

  useImperativeHandle(ref, () => ({
    getData: () => formData,
    getFilledFinishingWorks: () => {
      const predefined = formData.finishing_works.filter(fw => fw.qty && parseInt(fw.qty) > 0);
      const custom = formData.custom_works.filter(cw => cw.description.trim() && cw.qty && parseInt(cw.qty) > 0);
      return [...predefined, ...custom];
    }
  }));

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateFinishingWork = (index, field, value) => {
    setFormData(prev => {
      const works = [...prev.finishing_works];
      works[index] = { ...works[index], [field]: value };
      return { ...prev, finishing_works: works };
    });
  };

  const addCustomEntry = () => {
    setFormData(prev => ({
      ...prev,
      custom_works: [...prev.custom_works, { description: '', unit: 'Nos', qty: '', rate: '', details: '', isCustom: true }]
    }));
  };

  const updateCustomWork = (index, field, value) => {
    setFormData(prev => {
      const works = [...prev.custom_works];
      works[index] = { ...works[index], [field]: value };
      return { ...prev, custom_works: works };
    });
  };

  const removeCustomEntry = (index) => {
    setFormData(prev => ({
      ...prev,
      custom_works: prev.custom_works.filter((_, i) => i !== index)
    }));
  };

  return (
    <div className="bg-pink-50 border-2 border-gray-800 rounded-none font-mono text-sm">
      {/* Header */}
      <div className="bg-pink-400 text-white text-center py-2 font-bold text-lg border-b-2 border-gray-800">
        WORK DETAILS
      </div>

      {/* Top Section - WORK DETAILS */}
      <div className="border-b-2 border-gray-800 p-2">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-pink-100">
              <th className="border border-gray-400 p-2">CUTTING</th>
              <th className="border border-gray-400 p-2 w-24">QTY</th>
              <th className="border border-gray-400 p-2">Pressing</th>
              <th className="border border-gray-400 p-2 w-24">Qty</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-gray-400 p-2 text-center"></td>
              <td className="border border-gray-400 p-1">
                <Input 
                  type="number"
                  value={formData.cutting_qty}
                  onChange={(e) => updateField('cutting_qty', e.target.value)}
                  className="border-0 h-8 text-center"
                  data-testid="cutting-qty-input"
                />
              </td>
              <td className="border border-gray-400 p-2 text-center"></td>
              <td className="border border-gray-400 p-1">
                <Input 
                  type="number"
                  value={formData.pressing_qty}
                  onChange={(e) => updateField('pressing_qty', e.target.value)}
                  className="border-0 h-8 text-center"
                  data-testid="pressing-qty-input"
                />
              </td>
            </tr>
          </tbody>
        </table>
        <div className="flex items-center justify-end mt-2 gap-2">
          <span className="font-bold">TOTAL QTY:</span>
          <Input 
            type="number"
            value={formData.total_qty}
            onChange={(e) => updateField('total_qty', e.target.value)}
            className="w-24 h-8 text-center border-gray-400"
            data-testid="total-qty-input"
          />
        </div>
      </div>

      {/* FINISHING WORKS Section */}
      <div>
        <div className="bg-pink-300 p-2 font-bold text-center border-b border-gray-800">
          FINISHING WORKS
        </div>
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr className="bg-pink-100">
              <th className="border border-gray-400 p-1 text-left">DESCRIPTION</th>
              <th className="border border-gray-400 p-1 w-12"></th>
              <th className="border border-gray-400 p-1 w-20">QTY</th>
              <th className="border border-gray-400 p-1 w-20">RATE</th>
              <th className="border border-gray-400 p-1">DETAILS</th>
            </tr>
          </thead>
          <tbody>
            {/* Predefined items */}
            {formData.finishing_works.map((work, idx) => (
              <tr key={`pre-${idx}`} className={idx % 2 === 0 ? 'bg-white' : 'bg-pink-50'}>
                <td className="border border-gray-400 p-1 font-medium">{work.description}</td>
                <td className="border border-gray-400 p-1 text-center text-gray-500">{work.unit}</td>
                <td className="border border-gray-400 p-0">
                  <Input 
                    type="number"
                    value={work.qty}
                    onChange={(e) => updateFinishingWork(idx, 'qty', e.target.value)}
                    className="border-0 h-7 text-center text-xs rounded-none"
                    data-testid={`finishing-qty-${idx}`}
                  />
                </td>
                <td className="border border-gray-400 p-0">
                  <Input 
                    type="number"
                    value={work.rate}
                    onChange={(e) => updateFinishingWork(idx, 'rate', e.target.value)}
                    className="border-0 h-7 text-center text-xs rounded-none"
                    data-testid={`finishing-rate-${idx}`}
                  />
                </td>
                <td className="border border-gray-400 p-0">
                  <Input 
                    value={work.details}
                    onChange={(e) => updateFinishingWork(idx, 'details', e.target.value)}
                    className="border-0 h-7 text-xs rounded-none"
                    data-testid={`finishing-details-${idx}`}
                  />
                </td>
              </tr>
            ))}

            {/* Custom entries */}
            {formData.custom_works.map((work, idx) => (
              <tr key={`custom-${idx}`} className="bg-blue-50">
                <td className="border border-gray-400 p-0">
                  <Input
                    value={work.description}
                    onChange={(e) => updateCustomWork(idx, 'description', e.target.value)}
                    placeholder="Enter description..."
                    className="border-0 h-7 text-xs rounded-none font-medium"
                    data-testid={`custom-desc-${idx}`}
                  />
                </td>
                <td className="border border-gray-400 p-0">
                  <Select value={work.unit} onValueChange={(v) => updateCustomWork(idx, 'unit', v)}>
                    <SelectTrigger className="border-0 h-7 text-xs rounded-none px-1" data-testid={`custom-unit-${idx}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Nos">Nos</SelectItem>
                      <SelectItem value="Mts">Mts</SelectItem>
                      <SelectItem value="Box">Box</SelectItem>
                      <SelectItem value="Sqft">Sqft</SelectItem>
                      <SelectItem value="Rft">Rft</SelectItem>
                    </SelectContent>
                  </Select>
                </td>
                <td className="border border-gray-400 p-0">
                  <Input
                    type="number"
                    value={work.qty}
                    onChange={(e) => updateCustomWork(idx, 'qty', e.target.value)}
                    className="border-0 h-7 text-center text-xs rounded-none"
                    data-testid={`custom-qty-${idx}`}
                  />
                </td>
                <td className="border border-gray-400 p-0">
                  <Input
                    type="number"
                    value={work.rate}
                    onChange={(e) => updateCustomWork(idx, 'rate', e.target.value)}
                    className="border-0 h-7 text-center text-xs rounded-none"
                    data-testid={`custom-rate-${idx}`}
                  />
                </td>
                <td className="border border-gray-400 p-0">
                  <div className="flex items-center">
                    <Input
                      value={work.details}
                      onChange={(e) => updateCustomWork(idx, 'details', e.target.value)}
                      className="border-0 h-7 text-xs rounded-none flex-1"
                      data-testid={`custom-details-${idx}`}
                    />
                    <button
                      onClick={() => removeCustomEntry(idx)}
                      className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded flex-shrink-0"
                      title="Remove custom entry"
                      data-testid={`remove-custom-${idx}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Add Custom Entry Button */}
        <div className="p-2 border-t border-gray-400">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addCustomEntry}
            className="w-full border-dashed border-blue-400 text-blue-600 hover:bg-blue-50 hover:text-blue-700"
            data-testid="add-custom-finishing-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Custom Entry
          </Button>
        </div>
      </div>
    </div>
  );
});

WorkDetailsForm.displayName = 'WorkDetailsForm';

export default WorkDetailsForm;
